import {Form, Formik, FormikProps} from 'formik';
import {get, isEqual, set, some, sortBy, toLower, transform, values} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {settingsApi, userApi} from 'api';
import {
    Button, DataWrapper, PredefinedNotifierType,
    SelectFormik, UnsavedChangesLeavePrompt
} from 'components';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {MFAStatus, SecuritySettingsFormFields, UserSummary} from 'types';

import {SECURITY_FIELDS} from '../settings.constants';

import {CspWhitelist} from './CspWhitelist';
import {IpWhitelist} from './IpWhitelist';
import {LiveHelpCspWhitelist} from './LiveHelpCspWhitelist';
import {MultiFactorAuthentication} from './MultiFactorAuthentication';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SETTINGS;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type TemplateProps = {
    loading: boolean;
    reloadRequired: boolean;
    users: UserSummary[];
} & FormikProps<SecuritySettingsFormFields>

const FormTemplate = ({loading, dirty, reloadRequired, values: formValues, setFieldValue, initialValues, users}: TemplateProps) => {
    const {t} = useTranslation();
    const isSaveDisabled = useMemo<boolean>(() => isEqual(formValues, initialValues), [formValues, initialValues]);

    const appendDaysSuffix = useCallback((options: {id: any, name: string}[]) => options.map((option) => ({
        id: option.id,
        name: option.id ? `${option.name} ${toLower(t(COMMON_T_KEYS.ALL.DAYS))}` : option.name
    })), [t]);

    const passwordExpirationDaysOptions = useMemo(() => {
        const options = [
            {id: 30, name: '30'},
            {id: 60, name: '60'},
            {id: 90, name: '90'},
            {id: 180, name: '180'},
            {id: 365, name: '365'}
        ];

        if (formValues.passwordExpirationDays && !some(options, (option) => option.id === formValues.passwordExpirationDays)) {
            options.push({id: formValues.passwordExpirationDays, name: String(formValues.passwordExpirationDays)});
        }

        return appendDaysSuffix([
            {id: null, name: t(COMMON_T_KEYS.ALL.NONE)},
            ...sortBy(options, 'id')
        ]);
    }, [t, formValues, appendDaysSuffix]);

    const passwordExpireSoonDaysOptions = useMemo(() => {
        const options = [
            {id: 3, name: '3'},
            {id: 5, name: '5'},
            {id: 7, name: '7'},
            {id: 10, name: '10'}
        ];

        if (formValues.passwordExpireSoonDays && !some(options, (option) => option.id === formValues.passwordExpireSoonDays)) {
            options.push({id: formValues.passwordExpireSoonDays, name: String(formValues.passwordExpireSoonDays)});
        }

        return appendDaysSuffix([
            {id: null, name: t(COMMON_T_KEYS.ALL.NONE)},
            ...sortBy(options, 'id')
        ]);
    }, [formValues, t, appendDaysSuffix]);

    return (
        <Form>
            <div className='tw-flex sm:tw-w-96'>
                <SelectFormik
                    className='tw-w-1/2'
                    collapseEmptyOption={false}
                    placeholder={t(COMMON_T_KEYS.ALL.PASSWORD_EXPIRATION)}
                    name={SECURITY_FIELDS.PASSWORD_EXPIRATION_DAYS}
                    options={passwordExpirationDaysOptions}
                    valueAs='id'
                />
                <SelectFormik
                    collapseEmptyOption={false}
                    className='tw-w-1/2 tw-ml-4'
                    placeholder={t(COMMON_T_KEYS.ALL.PASSWORD_EXPIRE_SOON)}
                    name={SECURITY_FIELDS.PASSWORD_EXPIRE_SOON_DAYS}
                    options={passwordExpireSoonDaysOptions}
                    valueAs='id'
                />
            </div>
            <MultiFactorAuthentication mfaStatus={formValues.mfa.status} />
            <CspWhitelist value={formValues.cspWhitelist} users={users} className='tw-mt-8' setFieldValue={setFieldValue} />
            <LiveHelpCspWhitelist value={formValues.liveHelpCspWhitelist} users={users} className='tw-mt-8' setFieldValue={setFieldValue} />
            <IpWhitelist value={formValues.ipWhitelist} users={users} className='tw-mt-8' setFieldValue={setFieldValue} />
            <Button
                type='submit'
                color='primary'
                className='tw-mt-4 tw-mr-4'
                disabled={isSaveDisabled}
                loading={loading}
            >
                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
            </Button>
            {!dirty && reloadRequired &&
                <div className='tw-mt-2 tw-text-warning'>{t(T_KEYS.RELOAD_PAGE_MESSAGE)}</div>}
            <UnsavedChangesLeavePrompt values={formValues} initialValues={initialValues} />
        </Form>
    );
};

export const SecuritySettings: React.FC = () => {
    const [reloadRequired, setReloadRequired] = useState(false);
    const {send: getSettings, data: settings, loading: getLoading} = useApiData(settingsApi.getSecuritySettings, {loadingDefault: true});
    const {send: getUsers, data: users} = useApiData(userApi.getUsersSummary);

    const initialValues: SecuritySettingsFormFields = useMemo(() => ({
        passwordExpirationDays: settings?.passwordExpirationDays,
        passwordExpireSoonDays: settings?.passwordExpireSoonDays,
        ipWhitelist: settings?.ipWhitelist,
        liveHelpCspWhitelist: settings?.liveHelpCspWhitelist,
        cspWhitelist: settings?.cspWhitelist,
        mfa: {
            status: settings?.mfa?.status || MFAStatus.Disabled
        }
    }), [settings]);

    const onSaveSettingsSuccess = useCallback(() => {
        setReloadRequired(true);
        getSettings();
    }, [getSettings]);

    const {
        send: saveSettings,
        loading: saveInProgress,
    } = useApiData(settingsApi.saveSecuritySettings, {onSuccess: onSaveSettingsSuccess, notificationType: PredefinedNotifierType.Save});

    const handleSubmit = useCallback((formValues: SecuritySettingsFormFields) => {
        const data: Partial<SecuritySettingsFormFields> = transform(values(SECURITY_FIELDS), (acc, key) => {
            const value = get(formValues, key);
            if (value !== get(initialValues, key)) {
                set(acc, key, value);
            }
        }, {});

        saveSettings(data);
    }, [initialValues, saveSettings]);

    useEffect(() => {
        getSettings();
        getUsers();
    }, [getSettings, getUsers]);

    return (
        <div className='tw-relative'>
            <DataWrapper isLoading={getLoading || saveInProgress}>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    onSubmit={handleSubmit}
                >
                    {(props) => (
                        <FormTemplate
                            {...props}
                            users={users}
                            reloadRequired={reloadRequired}
                            loading={getLoading || saveInProgress}
                        />
                    )}
                </Formik>
            </DataWrapper>
        </div>
    );

};